'use client';

import React, { useRef, useEffect } from 'react';
import Link from 'next/link';
import { Bell, X, CheckCheck, AlertTriangle, Info, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { useNotifications, AppNotification } from '@/contexts/NotificationsContext';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `Il y a ${days}j`;
}

function NotifIcon({ type }: { type: AppNotification['type'] }) {
  const cls = 'w-4 h-4 flex-shrink-0 mt-0.5';
  if (type === 'warning') return <AlertTriangle className={cls} style={{ color: 'var(--warning, #f59e0b)' }} />;
  if (type === 'error') return <XCircle className={cls} style={{ color: 'var(--danger, #ef4444)' }} />;
  if (type === 'success') return <CheckCircle className={cls} style={{ color: 'var(--success, #22c55e)' }} />;
  return <Info className={cls} style={{ color: 'var(--primary)' }} />;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function NotificationsPanel({ open, onClose }: Props) {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden"
      style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', maxHeight: '480px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2">
          <Bell size={16} style={{ color: 'var(--foreground)' }} />
          <span className="font-600 text-sm" style={{ color: 'var(--foreground)' }}>Notifications</span>
          {unreadCount > 0 && (
            <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
              title="Tout marquer comme lu"
            >
              <CheckCheck size={14} style={{ color: 'var(--muted-foreground)' }} />
            </button>
          )}
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X size={14} style={{ color: 'var(--muted-foreground)' }} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <Bell size={28} style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Aucune notification</p>
          </div>
        ) : (
          <ul>
            {notifications.map(n => (
              <li
                key={n.id}
                className="group flex items-start gap-3 px-4 py-3 border-b transition-colors hover:bg-muted/40 cursor-pointer"
                style={{
                  borderColor: 'var(--border)',
                  backgroundColor: n.read ? 'transparent' : 'rgba(var(--primary-rgb, 59,130,246), 0.04)',
                }}
                onClick={() => { if (!n.read) markAsRead(n.id); }}
              >
                <NotifIcon type={n.type} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-1">
                    <p className="text-xs font-600 leading-tight" style={{ color: 'var(--foreground)' }}>{n.title}</p>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: 'var(--primary)' }} />
                    )}
                  </div>
                  <p className="text-2xs mt-0.5 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{n.message}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-2xs" style={{ color: 'var(--muted-foreground)', opacity: 0.7 }}>{timeAgo(n.createdAt)}</span>
                    {n.link && (
                      <Link href={n.link} onClick={onClose} className="text-2xs font-500 hover:underline" style={{ color: 'var(--primary)' }}>
                        Voir →
                      </Link>
                    )}
                  </div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); deleteNotification(n.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted transition-all flex-shrink-0"
                  title="Supprimer"
                >
                  <Trash2 size={12} style={{ color: 'var(--muted-foreground)' }} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
